import { DataTable } from "@quake/shared-ui/components/dataTable/dataTable";
import { Button } from "@quake/shared-ui/components/ui/button";
import { toast } from "@quake/shared-ui/lib/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { trpc } from "~/utils/trpc";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import CheckCircle from "lucide-react/dist/esm/icons/check-circle";
import CircleXIcon from "lucide-react/dist/esm/icons/circle-x";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import DOMPurify from "dompurify";

export const Route = createFileRoute("/_dashboard/$datasetSlug/settings/email-settings/templates/")({
  component: RouteComponent,
});

const defaultEmailSetting = { id: "", name: "" };
function RouteComponent() {
  const { data, status } = useQuery(trpc.emailSettings.getAll.queryOptions());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { datasetSlug } = Route.useParams();
  const [currentEmailSetting, setCurrentEmailSetting] = useState(defaultEmailSetting);
  const queryClient = useQueryClient();
  const deleteEmailSetting = useMutation(
    trpc.emailSettings.delete.mutationOptions({
      onSuccess: async (res) => {
        toast.success("Deleted successfully");
        await queryClient.invalidateQueries({ queryKey: trpc.emailSettings.getAll.pathKey() });
      },
      onError: (error) => {
        toast.error(error.message);
      },
      onSettled: () => {
        setCurrentEmailSetting(defaultEmailSetting);
        setDeleteDialogOpen(false);
      },
    }),
  );

  useBreadCrumbs([
    {
      title: "Email Settings",
      link: `/${datasetSlug}/settings/email-settings`,
    },
    {
      title: "Email Templates",
      link: `/${datasetSlug}/settings/email-settings/templates`,
    },
  ]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: trpc.emailSettings.getAll.pathKey() });
  }, []);

  return (
    <div>
      <div className="text-right">
        <Link to="/$datasetSlug/settings/email-settings/templates/create" params={{ datasetSlug }}>
          <Button>Create Email Template</Button>
        </Link>
      </div>
      <DataTable
        initialColumnVisibility={{ id: false }}
        data={data?.data ?? []}
        isLoading={status === "pending"}
        showPagination={true}
        showSearchFilter={false}
        showColumnFilter
        manualSorting={true}
        columns={[
          { accessorKey: "name", header: "Name" },
          {
            accessorKey: "subject",
            header: "Subject",
            cell: ({ row }) => <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.original.subject) }} />,
          },
          {
            accessorKey: "active",
            header: "Enabled",
            cell: ({ row }) =>
              row.original.active ? (
                <CheckCircle size={20} className="text-green-500" />
              ) : (
                <CircleXIcon size={20} className="text-red-500" />
              ),
          },
          {
            header: "Actions",
            cell: ({ row }) => (
              <>
                <div className="flex">
                  <Trash2
                    onClick={() => {
                      setCurrentEmailSetting(row.original);
                      setDeleteDialogOpen(true);
                    }}
                    size={20}
                    className="mr-2 text-destructive cursor-pointer"
                  />
                  <Link
                    to="/$datasetSlug/settings/email-settings/templates/edit/$id"
                    params={{ datasetSlug, id: row.original.id }}
                  >
                    <EditIcon size={20} className="mr-2 text-green-500 cursor-pointer" />
                  </Link>
                </div>
              </>
            ),
          },
        ]}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Email Setting</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <b>{currentEmailSetting.name}</b>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (currentEmailSetting.id) {
                  deleteEmailSetting.mutate({ id: currentEmailSetting.id });
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
